// ── Credits about to expire, said the same way everywhere ───────────────────
//
// The same warning appears in three places: the notification bell, a banner on
// the Dashboard, and a banner above the balance card. They must agree exactly —
// three surfaces each phrasing the same fact differently is the bug this
// codebase keeps having to fix, and it is worse for a warning than for anything
// else, because a reader who sees two versions trusts neither.
//
// Built on buildBreakdownRows, which already groups the lots, flags the ones
// inside the window and sorts soonest-first. Reusing it means the banner and
// the red row in the breakdown panel can never disagree about which batch is at
// risk — they are literally the same computation.

import { buildBreakdownRows } from './creditBreakdown';

/** What each kind of credit is called in the sentence. */
const SOURCE_NOUN = {
  plan: 'plan',
  purchased: 'purchased',
};

/**
 * One warning per batch of credits expiring inside the window.
 *
 * Ordered soonest-first, inherited from buildBreakdownRows. Two kinds expiring
 * within a fortnight of each other therefore produce two warnings with the more
 * urgent one first — which is what the reader needs to act on, and what the
 * spend order (first-expiring-first) will actually do.
 *
 * @param lots  [{ source, expires_on, remaining }] from GET /users/credits/:id
 * @param now   injectable, so the boundary is testable
 * @returns [{ key, source, credits, expiresOn, expiresLabel, daysUntilExpiry, message }]
 */
export function buildCreditExpiryWarnings(lots, now = new Date()) {
  return buildBreakdownRows(lots, now)
    .filter((row) => row.expiringSoon && row.credits !== 0)
    .map((row) => ({
      key: `credit-expiry:${row.key}`,
      source: row.source,
      credits: row.remaining,
      expiresOn: row.expiresOn,
      expiresLabel: row.expiresLabel,
      daysUntilExpiry: row.daysUntilExpiry,
      message: expiryMessage(row),
    }));
}

/**
 * The sentence.
 *
 * Three clauses, each doing one job: what is going, by when, and why acting
 * now is the right move. The last clause is not filler — without it, somebody
 * holding two batches cannot tell whether spending will touch the expiring one,
 * and might reasonably assume it will not.
 */
function expiryMessage(row) {
  const noun = SOURCE_NOUN[row.source] ?? SOURCE_NOUN.purchased;
  const amount = Number(row.remaining || 0).toLocaleString();
  const by = row.expiresLabel;
  return by
    ? `${amount} ${noun} credits expire soon. Use them before ${by} — credits expiring soonest are used first.`
    : `${amount} ${noun} credits expire soon — credits expiring soonest are used first.`;
}

/** True when there is anything to warn about, for callers that only need the flag. */
export const hasExpiringCredits = (lots, now = new Date()) =>
  buildCreditExpiryWarnings(lots, now).length > 0;
